import { get, cloneDeep, sortBy, orderBy } from 'lodash';
import { getPriceAndBonus } from "@/store/utils";
import {
  HOTELS,
} from "@/store/game/constants";
import {createPlayerStocks} from "@/store/game/schema";

export default  {
  gameId: (state) => state.gameState.gameId,
  localStoragePlayerId: (state, getters) => {
    const game = state.localStorage.find((game) => {
      return game.gameId === getters.gameId;
    });

    if (!game) {
      return false;
    }
    return game.playerId;
  },


  // Handle lobby
  isPlayerInGame: (state, getters) => getters.players[getters.localStoragePlayerId] || false,
  inLobby: (state) => !state.gameState.hasStarted,
  numberOfPlayers: (state) => state.gameState.numberOfPlayers,

  canRejoinGame: (state, getters) => {
    const inGameAlready = getters.players[getters.localStoragePlayerId];
    if (inGameAlready) {
      return false;
    }

    const hasAvailableSpace = Object.values(getters.players).some((player) => {
      return player.isOnline === false;
    });

    // See if there are empty slots
    if (!hasAvailableSpace) {
      return false;
    }

    return true;
  },

  tilesPerHotel: (state, getters) => {
    return Object.values(getters.board).reduce((obj, tile) => {
      if (!obj[tile.hotel]) {
        obj[tile.hotel] = [];
      }
      obj[tile.hotel].push(tile.id);
      return obj;
    }, {});
  },

  /**
   // In the case of a tie for the title of largest shareholder, the first and second bonuses are combined and divided equally between the tying shareholders.
   // In the case of a tie for the title of second largest shareholder, the second bonus is divided equally between the tying players.
   // If only one player holds stock in the defunct chain, he receives both bonuses.
   * @param state
   * @param getters
   * @returns {*}
   */
  majorityHoldersPerHotel: (state, getters) => {
    return HOTELS.reduce((obj, hotel) => {
      if (!obj[hotel]) {
        obj[hotel] = {};
      }

      const orderedPlayers = orderBy(getters.players, [`stocks[${hotel}]`], ['desc']);

      const first = [];
      let firstCount = 0;
      const second = [];
      let secondCount = 0;

      function next() { return false }  // next iteration of loop
      function exitLoop() { return true } // exit entire loop

      orderedPlayers.some((player) => {
          if (player.stocks[hotel] <= 0) {
            return next();
          }

          if (first.length === 0) {
            first.push(player.id);
            firstCount = player.stocks[hotel];
            return next();
          }

          // tie for first place
          if (player.stocks[hotel] === firstCount) {
            first.push(player.id);
            return next();
          }

          // If more than one person is tied for first, stop.
          // First and second place will be split equally between players. No second place given
          if (first.length > 1) {
            return exitLoop();
          }

          if (second.length === 0){
            second.push(player.id);
            secondCount = player.stocks[hotel];
            return next();
          }

          if (player.stocks[hotel] === secondCount) {
            second.push(player.id);
            return next();
          }
      });

      obj[hotel] = {
        first,
        second,
      };
      return obj;
    }, {});
  },

  hotelsAvailableForNewCompany: (state, getters) => {
    return HOTELS.filter((hotel) => {
      if (!getters.tilesPerHotel[hotel]) {
        return hotel;
      }
      return false;
    });
  },

  stockMoneyPerHotel: (state, getters) => {
    return getters.stocksSortedByStartingPrice.reduce((obj, stock) => {
      const tilesPerHotel = getters.tilesPerHotel[stock.id];
      if (!tilesPerHotel) {
        obj[stock.id] = getPriceAndBonus(stock, 0);
        return obj;
      }

      const count = tilesPerHotel.length;
      obj[stock.id] = getPriceAndBonus(stock, count);
      return obj;
    }, {});
  },

  totalPrice: (state, getters) => {
    return getters.stocksToBePurchased.reduce((acc, stockId) => {
      return acc + getters.stockMoneyPerHotel[stockId].price;
    }, 0);
  },

  gameRef: (state) => state.gameRef,
  playerRef: (state) => state.playerRef,
  players: (state) => get(state, 'gameState.players', {}),
  playerOrder: (state) => state.gameState.playerOrder || [],
  // Player order, but [0] = the current player
  playerOrderReindexed: (state, getters) => {
    const reindexed = cloneDeep(getters.playerOrder);

    getters.playerOrder.some(playerId => {
      if (playerId === getters.localPlayerId) {
        // exit the loop, current player is on top now
        return true;
      }

      const player = reindexed.shift();
      reindexed.push(player);
    });
    // get the current player index. Then loop and shift/push off rows until 0 = current player ID
    return reindexed;
  },
  nextPlayerId: (state, getters) => {
    const playerOrder = getters.playerOrder;
    const localPlayerStocks = playerOrder.indexOf(getters.localPlayerId);
    let nextPlayerId = playerOrder[localPlayerStocks + 1];
    if (!nextPlayerId) {
      nextPlayerId = playerOrder[0];
    }
    return nextPlayerId;
  },
  stocks: (state) => state.gameState.stocks,
  stocksSortedByStartingPrice: (state) => {
    return sortBy(state.gameState.stocks, [
      (stock) => {
        return stock.startingPrice;
      }
    ]);
  },

  // Merger
  isMerger: (state) => state.gameState.merger,
  mergerDetails: (state) => state.gameState.merger,
  mergingHotel: (state, getters) => getters.mergerDetails.hotelsToBeMerged[getters.mergerDetails.mergingHotelIndex],
  mergingNextTurnPlayerIds: (state, getters) => getters.mergingHotel.nextTurnPlayerIds || [],
  mergingPlayerId: (state, getters) => getters.mergingHotel.nextTurnPlayerIds[0],
  mergingHotelId: (state, getters) => getters.mergingHotel.hotelId,
  survivingHotelId: (state, getters) => getters.mergerDetails.survivingHotelId,

  localPlayer: (state, getters) => getters.players[getters.localStoragePlayerId],
  localPlayerId: (state, getters) => getters.localPlayer.id,
  localPlayerStocks: (state, getters) => getters.localPlayer.stocks || createPlayerStocks(),
  localPlayerTiles: (state, getters) => get(getters, 'localPlayer.tiles', []),
  localStorage: (state) => state.localStorage,
  hasGameStarted: (state) => state.gameState.hasStarted,
  stocksToBePurchased: (state) => state.gameState.stocksToBePurchased || [],
  board: (state) => state.gameState.board,
  tiles: (state) => state.gameState.tiles,
  selectedTile: (state) => state.gameState.selectedTile,
  whosTurn: (state) => state.gameState.whosTurn,
  // The player object for 'whos turn'
  currentPlayer: (state, getters) => get(getters, `players[${getters.whosTurn}]`, {}),

  // Game step states
  isMyTurn: (state, getters) => getters.whosTurn === get(getters, 'localPlayerId', false),
  hasPlacedTile: (state) => state.gameState.selectedTile !== false,
  isNewCompany: (state) => state.gameState.newCompany !== false,
  isBuyingStocks: (state, getters) => getters.hasPlacedTile
    && !getters.isMerger
    && !getters.isNewCompany
    && getters.isMyTurn,
}
